/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * The draft/value translation behind {@link NumberField}, kept pure so the rule that
 * actually matters — an empty box reports `undefined`, never `0` — is testable without
 * a DOM.
 */

/** How a value from the parent renders as text. `undefined` is an empty box. */
export function numberToDraft(value: number | undefined, minDecimals: number | null = null): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '';
  return minDecimals === null ? String(value) : padDecimals(value, minDecimals);
}

/**
 * **At least `minDecimals` places, and never fewer than the value has** (`3c-ii`, Todd: *"currency
 * fields drop trailing cents... 8.00 becomes 8"*).
 *
 * `String(8)` is `'8'`, so a price saved as `8.00` came back as `8` the next time the box was drawn.
 * Padding rather than `toFixed`, because `toFixed` **rounds**: a CPM of `8.125` is a real price and
 * must not reopen as `8.13`, which would then be saved as a different number.
 */
export function padDecimals(value: number, minDecimals: number): string {
  const text = String(value);
  if (!Number.isFinite(value) || /e/i.test(text)) return text;
  const places = text.includes('.') ? text.length - text.indexOf('.') - 1 : 0;
  return places >= minDecimals ? text : value.toFixed(minDecimals);
}

/**
 * The draft as it should read once the operator leaves the box: padded where it parses, untouched
 * where it does not, so a half-typed value is never rewritten into something else.
 */
export function settleDraft(draft: string, minDecimals: number | null): string {
  if (minDecimals === null) return draft;
  const parsed = parseNumberDraft(draft);
  return parsed === undefined ? draft : padDecimals(parsed, minDecimals);
}

/**
 * What the operator's text means.
 *
 * Empty is `undefined` and **not** `0`: `Number('')` returning `0` is the whole defect
 * — it wrote a zero back into the form, which re-rendered as a `0` that backspace could
 * not remove. Unparseable text is `undefined` too, so a lone `-` or `1e` reports "no
 * value yet" while the draft keeps the characters on screen.
 */
export function parseNumberDraft(draft: string): number | undefined {
  const trimmed = draft.trim();
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * Should the parent's value overwrite the text on screen?
 *
 * Only when the two genuinely disagree. `"2000."` already *means* 2000, so re-syncing
 * on it would delete the decimal point the instant it was typed — the same class of
 * interference as the `0` this component exists to remove.
 */
export function shouldResyncDraft(draft: string, value: number | undefined): boolean {
  return parseNumberDraft(draft) !== value;
}
